/* eslint-disable react/no-unescaped-entities */
/**
 * Master Trilogy Certificate — issued only when all three Healing Trilogy
 * programs (The Me I See, Healing WYCF, She Is) are complete for the same
 * client email. Backend enforces this at
 * `/api/coaching/trilogy/certificate/confirm-name`.
 *
 * Public route: /coaching/trilogy/certificate
 * Client enters their email → we check dashboard endpoint → if all 3
 * complete, show the certificate + name confirm + print. Otherwise show
 * gate copy pointing them back to their dashboard.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Printer, Award } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const C = {
  purple: "#5B2C83",
  purpleBold: "#6E37A0",
  purpleDeep: "#3D1D5C",
  green: "#7FA66A",
  greenBold: "#6C9459",
  border: "#ECE5F2",
  textDim: "#6B6472",
  textFaint: "#A69FB0",
};

export default function TrilogyCertificate() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState(null); // { trilogy_complete, programs, incomplete_slug }
  const [error, setError] = useState("");
  const [confirmed, setConfirmed] = useState(false);

  async function check() {
    setLoading(true);
    setError("");
    try {
      const r = await fetch(`${API}/api/coaching/dashboard/for-client`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      if (!r.ok) throw new Error("Lookup failed. Please check your email.");
      const data = await r.json();
      setState(data);
    } catch (e) {
      setError(e.message || "Something went wrong.");
    }
    setLoading(false);
  }

  async function confirmAndPrint() {
    if (!name.trim() || !state?.trilogy_complete) return;
    setLoading(true);
    setError("");
    try {
      const r = await fetch(
        `${API}/api/coaching/trilogy/certificate/confirm-name?name=${encodeURIComponent(name.trim())}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: email.trim().toLowerCase() }),
        }
      );
      if (!r.ok) {
        const detail = await r.json().catch(() => ({}));
        throw new Error(
          detail?.detail?.code === "trilogy_not_complete"
            ? `You have not yet completed ${detail.detail.incomplete_slug}. Return to your dashboard to continue.`
            : "Confirmation failed."
        );
      }
      setConfirmed(true);
      setTimeout(() => window.print(), 400);
    } catch (e) {
      setError(e.message || "Something went wrong.");
    }
    setLoading(false);
  }

  const programsFallback = ["the-me-i-see", "healing-what-you-cannot-feel", "she-is"];
  const programTitle = {
    "the-me-i-see": "The Me I See™",
    "healing-what-you-cannot-feel": "Healing What You Cannot Feel™",
    "she-is": "She Is™",
  };

  return (
    <div
      data-testid="trilogy-cert-page"
      className="min-h-screen"
      style={{ background: "#FAF7F5", fontFamily: "'Inter', sans-serif" }}
    >
      <div className="max-w-3xl mx-auto pt-16 pb-20 px-6">
        {!state && (
          <div
            className="rounded-3xl p-10 sm:p-12 text-center"
            style={{
              background: "#fff",
              border: `1px solid ${C.border}`,
              boxShadow: "0 20px 50px rgba(91,44,131,0.14)",
            }}
          >
            <div
              className="w-14 h-14 rounded-full mx-auto flex items-center justify-center mb-5"
              style={{ background: `linear-gradient(135deg, ${C.purple}, ${C.green})` }}
            >
              <Award className="w-7 h-7 text-white" />
            </div>
            <div
              className="text-[11px] uppercase tracking-[0.28em] mb-2"
              style={{ color: C.purpleBold, fontWeight: 700 }}
            >
              KCIS · Master Trilogy
            </div>
            <h1
              className="mb-3"
              style={{ fontFamily: "'Playfair Display', serif", fontSize: 32, color: "#111" }}
            >
              Master Trilogy Certificate
            </h1>
            <p style={{ color: C.textDim, fontSize: 15, lineHeight: 1.75, maxWidth: 480, margin: "0 auto" }}>
              For the woman who has completed all three programs — The Me I See, Healing What You
              Cannot Feel, and She Is. Enter your enrollment email to check your progress.
            </p>
            <div className="mt-6 flex justify-center gap-3 flex-wrap">
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                data-testid="trilogy-email-input"
                className="focus:outline-none"
                style={{
                  padding: "12px 18px",
                  border: `2px solid ${C.border}`,
                  borderRadius: 999,
                  fontSize: 14,
                  minWidth: 260,
                }}
              />
              <button
                onClick={check}
                disabled={loading || !email.includes("@")}
                data-testid="trilogy-check-btn"
                className="rounded-full text-white disabled:opacity-40 inline-flex items-center gap-2"
                style={{
                  background: `linear-gradient(135deg, ${C.purpleBold}, ${C.purpleDeep})`,
                  padding: "12px 24px",
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Check my status"}
              </button>
            </div>
            {error && (
              <div className="mt-4 text-sm" style={{ color: "#B91C1C" }} data-testid="trilogy-error">
                {error}
              </div>
            )}
            <div className="mt-6 text-xs" style={{ color: C.textFaint }}>
              <Link to="/coaching/dashboard" className="underline" data-testid="trilogy-dashboard-link">
                Go to my coaching dashboard →
              </Link>
            </div>
          </div>
        )}

        {state && !state.trilogy_complete && (
          <div
            data-testid="trilogy-locked"
            className="rounded-3xl p-10 sm:p-12"
            style={{
              background: "#fff",
              border: `1px solid ${C.border}`,
              boxShadow: "0 20px 50px rgba(91,44,131,0.14)",
            }}
          >
            <div
              className="text-[11px] uppercase tracking-[0.28em] mb-2"
              style={{ color: C.purpleBold, fontWeight: 700 }}
            >
              Almost there
            </div>
            <h2
              className="mb-3"
              style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, color: "#111" }}
            >
              Your Master Trilogy Certificate is waiting.
            </h2>
            <p style={{ color: C.textDim, fontSize: 14.5, lineHeight: 1.7 }}>
              This certificate is issued once all three programs are complete. Here's where you stand:
            </p>
            <ul className="mt-4 space-y-2">
              {(state.programs || programsFallback.map((s) => ({ program_slug: s }))).map((p, i) => {
                const slug = p.program_slug || programsFallback[i];
                const done = p.program_completed;
                const enrolled = p.enrolled;
                return (
                  <li
                    key={slug}
                    className="flex items-center justify-between rounded-xl px-4 py-3"
                    style={{ border: `1px solid ${C.border}` }}
                    data-testid={`trilogy-status-${slug}`}
                  >
                    <span style={{ fontWeight: 600 }}>{programTitle[slug]}</span>
                    <span
                      className="text-xs rounded-full px-3 py-1"
                      style={{
                        background: done ? "rgba(127,166,106,0.18)" : "rgba(155,89,217,0.10)",
                        color: done ? C.greenBold : C.purpleBold,
                        fontWeight: 700,
                      }}
                    >
                      {done ? "Complete" : enrolled ? `${p.progress_pct || 0}% in progress` : "Not enrolled"}
                    </span>
                  </li>
                );
              })}
            </ul>
            <div className="mt-6 flex gap-3 flex-wrap">
              <Link to="/coaching/dashboard" data-testid="trilogy-go-dashboard">
                <button
                  className="rounded-full text-white inline-flex items-center gap-2"
                  style={{
                    background: `linear-gradient(135deg, ${C.purpleBold}, ${C.purpleDeep})`,
                    padding: "12px 22px",
                    fontSize: 13,
                    fontWeight: 700,
                  }}
                >
                  Continue in my dashboard
                </button>
              </Link>
              <button
                onClick={() => {
                  setState(null);
                  setEmail("");
                }}
                className="rounded-full inline-flex items-center gap-2"
                style={{
                  background: "#fff",
                  border: `2px solid ${C.purpleBold}`,
                  color: C.purpleBold,
                  padding: "12px 22px",
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                Check a different email
              </button>
            </div>
          </div>
        )}

        {state?.trilogy_complete && (
          <div data-testid="trilogy-certificate-panel">
            <div
              className="text-center relative overflow-hidden rounded-[28px]"
              style={{
                background: `linear-gradient(150deg, ${C.purpleDeep} 0%, ${C.purpleBold} 55%, ${C.green} 100%)`,
                padding: "70px 46px",
                boxShadow: "0 30px 70px rgba(91,44,131,0.35)",
                color: "#fff",
              }}
              data-testid="trilogy-certificate-doc"
            >
              <div
                style={{
                  position: "absolute",
                  width: 340,
                  height: 340,
                  borderRadius: "50%",
                  background: `radial-gradient(circle, rgba(255,255,255,0.16), transparent 70%)`,
                  top: -160,
                  right: -80,
                }}
              />
              <div className="relative">
                <div
                  className="w-[80px] h-[80px] rounded-full mx-auto flex items-center justify-center mb-6"
                  style={{
                    background: "#fff",
                    color: C.purpleDeep,
                    fontFamily: "'Playfair Display', serif",
                    fontWeight: 800,
                    fontSize: 26,
                    boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
                  }}
                >
                  CV
                </div>
                <div
                  style={{
                    fontSize: 11,
                    letterSpacing: "3px",
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,0.72)",
                  }}
                >
                  This Certifies That
                </div>
                <div
                  className="my-3"
                  style={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: 40,
                    fontWeight: 800,
                    color: "#fff",
                  }}
                  data-testid="trilogy-cert-name"
                >
                  {name || "Your Name"}
                </div>
                <div style={{ fontSize: 14, color: "rgba(255,255,255,0.85)" }}>
                  has completed all three programs of
                </div>
                <div
                  style={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: 28,
                    color: "#fff",
                    margin: "10px 0 6px",
                    fontWeight: 800,
                  }}
                >
                  The Healing Trilogy
                </div>
                <div
                  style={{
                    fontSize: 12.5,
                    color: "rgba(255,255,255,0.85)",
                    fontStyle: "italic",
                  }}
                >
                  The Me I See™ · Healing What You Cannot Feel™ · She Is™
                </div>
                <div style={{ height: 1, background: "rgba(255,255,255,0.25)", margin: "34px 0" }} />
                <div
                  style={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: 18,
                    fontWeight: 700,
                    color: "#fff",
                  }}
                >
                  Dr. Christy Vonta
                </div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.7)", marginTop: 3 }}>
                  Christy Vonta Coaching &amp; Consulting · KCIS Coaching Door
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-center gap-3 flex-wrap print:hidden">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Type your full name"
                data-testid="trilogy-name-input"
                className="focus:outline-none"
                style={{
                  padding: "13px 18px",
                  border: `2px solid ${C.border}`,
                  borderRadius: 999,
                  fontSize: 14,
                  minWidth: 260,
                }}
              />
              <button
                onClick={confirmAndPrint}
                disabled={!name.trim() || loading}
                data-testid="trilogy-print-btn"
                className="rounded-full text-white disabled:opacity-40 inline-flex items-center gap-2"
                style={{
                  background: `linear-gradient(135deg, ${C.purpleBold}, ${C.green})`,
                  padding: "13px 24px",
                  fontSize: 13.5,
                  fontWeight: 800,
                  boxShadow: "0 8px 24px rgba(127,166,106,0.4)",
                }}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Confirming…
                  </>
                ) : (
                  <>
                    <Printer className="w-4 h-4" /> Confirm &amp; download
                  </>
                )}
              </button>
            </div>
            {confirmed && (
              <div
                className="mt-3 text-center text-sm"
                style={{ color: C.greenBold, fontWeight: 700 }}
                data-testid="trilogy-confirmed-note"
              >
                Confirmed. Your Master Trilogy Certificate is ready to print.
              </div>
            )}
            {error && (
              <div className="mt-3 text-center text-sm" style={{ color: "#B91C1C" }} data-testid="trilogy-error">
                {error}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
